(() => {
  'use strict';
  if (customElements.get('cm-leather-atelier')) return;
  const openInstances = new Set();
  let savedOverflow = '';
  let savedOverflowPriority = '';
  const lockScroll = instance => {
    if (openInstances.size === 0) {
      savedOverflow = document.body.style.getPropertyValue('overflow');
      savedOverflowPriority = document.body.style.getPropertyPriority('overflow');
      document.body.style.setProperty('overflow', 'hidden');
    }
    openInstances.add(instance);
  };
  const unlockScroll = instance => {
    openInstances.delete(instance);
    if (openInstances.size === 0 && document.body.style.getPropertyValue('overflow') === 'hidden') {
      if (savedOverflow) document.body.style.setProperty('overflow', savedOverflow, savedOverflowPriority);
      else document.body.style.removeProperty('overflow');
    }
  };
  class LeatherAtelier extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.cleanups = [];
      const { signal } = this.controller;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelectorAll('.gallery-group').forEach(group => {
        const track = group.querySelector('.photo-track');
        const photographs = [...track.querySelectorAll('.gallery-photo')];
        if (!photographs.length) return;
        const count = group.querySelector('.photo-count b');
        const thumbnails = [...group.querySelectorAll('.photo-thumb')];
        let active = 0;
        let pending = null;
        let frame = 0;
        let settleTimer = 0;
        const updateControls = () => {
          if (count) count.textContent = String(active + 1);
          thumbnails.forEach((thumb, index) => thumb.setAttribute('aria-pressed', String(index === active)));
          photographs.forEach((photo, index) => {
            photo.setAttribute('aria-hidden', String(index !== active));
            photo.querySelector('.photo-trigger').tabIndex = index === active ? 0 : -1;
          });
        };
        const sync = () => {
          frame = 0;
          const origin = track.getBoundingClientRect().left;
          let distance = Infinity;
          let nearest = active;
          photographs.forEach((photo, index) => {
            const delta = Math.abs(photo.getBoundingClientRect().left - origin);
            if (delta < distance) { nearest = index; distance = delta; }
          });
          if (pending !== null && nearest !== pending) return;
          active = nearest;
          updateControls();
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
        const moveTo = index => {
          const hadPhotoFocus = photographs.some(photo => photo.contains(document.activeElement));
          active = (index + photographs.length) % photographs.length;
          pending = active;
          updateControls();
          if (hadPhotoFocus) photographs[active].querySelector('.photo-trigger').focus({ preventScroll: true });
          track.scrollTo({ left: photographs[active].offsetLeft - photographs[0].offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        };
        group.querySelector('.photo-prev')?.addEventListener('click', () => moveTo((pending ?? active) - 1), { signal });
        group.querySelector('.photo-next')?.addEventListener('click', () => moveTo((pending ?? active) + 1), { signal });
        thumbnails.forEach(thumb => thumb.addEventListener('click', () => moveTo(Number(thumb.dataset.photoTo)), { signal }));
        track.addEventListener('scroll', () => {
          schedule();
          clearTimeout(settleTimer);
          settleTimer = setTimeout(() => { pending = null; schedule(); }, 140);
        }, { passive: true, signal });
        track.addEventListener('keydown', event => {
          if (photographs.length < 2 || !(event.target === track || event.target.closest('.photo-trigger'))) return;
          const moves = { ArrowRight: (pending ?? active) + 1, ArrowLeft: (pending ?? active) - 1, Home: 0, End: photographs.length - 1 };
          if (!(event.key in moves)) return;
          event.preventDefault();
          moveTo(moves[event.key]);
        }, { signal });
        let start = null;
        let moved = false;
        let suppressUntil = 0;
        let scrollAtStart = 0;
        track.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0) return;
          start = { x: event.clientX, y: event.clientY };
          moved = false;
          scrollAtStart = track.scrollLeft;
        }, { passive: true, signal });
        track.addEventListener('pointermove', event => {
          if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 9) moved = true;
        }, { passive: true, signal });
        const release = () => {
          if (start && (moved || Math.abs(track.scrollLeft - scrollAtStart) > 4)) suppressUntil = performance.now() + 450;
          start = null;
        };
        track.addEventListener('pointerup', release, { passive: true, signal });
        track.addEventListener('pointercancel', release, { passive: true, signal });
        track.addEventListener('pointerleave', release, { passive: true, signal });
        track.addEventListener('click', event => {
          if (event.detail > 0 && performance.now() < suppressUntil) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, { capture: true, signal });
        const observer = new ResizeObserver(schedule);
        observer.observe(track);
        updateControls();
        schedule();
        this.cleanups.push(() => {
          observer.disconnect();
          cancelAnimationFrame(frame);
          clearTimeout(settleTimer);
        });
      });
      this.dialog = this.querySelector('.photo-dialog');
      const dialogImage = this.dialog.querySelector('.dialog-image');
      const dialogError = this.dialog.querySelector('.dialog-error');
      const dialogTitle = this.dialog.querySelector('.dialog-top h2');
      const dialogCaption = this.dialog.querySelector('.dialog-caption');
      const cleanupDialog = () => {
        if (this.scrollLocked) { unlockScroll(this); this.scrollLocked = false; }
        dialogImage.removeAttribute('src');
        dialogImage.hidden = false;
        dialogError.hidden = true;
        if (this.isConnected && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
        this.returnFocus = null;
      };
      this.addEventListener('click', event => {
        if (event.defaultPrevented || !(event.target instanceof Element)) return;
        const trigger = event.target.closest('.photo-trigger');
        if (!trigger || !this.contains(trigger) || this.dialog.open) return;
        dialogImage.hidden = false;
        dialogError.hidden = true;
        dialogImage.alt = trigger.dataset.photoAlt;
        dialogImage.width = Number(trigger.dataset.photoWidth);
        dialogImage.height = Number(trigger.dataset.photoHeight);
        dialogTitle.textContent = trigger.dataset.photoLeather;
        dialogCaption.textContent = trigger.dataset.photoCaption;
        this.returnFocus = trigger;
        this.dialog.showModal();
        lockScroll(this);
        this.scrollLocked = true;
        dialogImage.src = trigger.dataset.photoSrc;
      }, { signal });
      dialogImage.addEventListener('error', () => {
        if (!this.dialog.open) return;
        dialogImage.hidden = true;
        dialogError.hidden = false;
      }, { signal });
      this.dialog.querySelector('.dialog-close').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', cleanupDialog, { signal });
      this.cleanups.push(cleanupDialog);
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      if (this.dialog?.open) this.dialog.close();
      this.cleanups.forEach(cleanup => cleanup());
      this.cleanups = [];
      this.controller = null;
    }
  }
  customElements.define('cm-leather-atelier', LeatherAtelier);
})();
